import type { CSSProperties, ReactNode } from 'react';

/**
 * Small repeated shapes from the design that are not design-system components:
 * the eyebrow micro-label, the section heading pair, and the screen padding.
 */

/** Tiny uppercase label above a heading, e.g. "Kindergarten · 通園準備". */
export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="eyebrow" style={style}>
      {children}
    </div>
  );
}

/** Screen title block: eyebrow, Japanese h1, optional English subtitle. */
export function ScreenTitle({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: subtitle ? 'flex-start' : 'flex-end',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        marginBottom: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'var(--text-2xl)',
            margin: '3px 0 0',
          }}
        >
          {title}
        </h1>
        {subtitle ? (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
            {subtitle}
          </div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

/** Heading over a list or card group, with an optional right-aligned note. */
export function SectionHeading({
  children,
  note,
  style,
}: {
  children: ReactNode;
  note?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        marginBottom: 10,
        ...style,
      }}
    >
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 'var(--text-lg)',
          margin: 0,
        }}
      >
        {children}
      </h3>
      {note ? <Eyebrow>{note}</Eyebrow> : null}
    </div>
  );
}

/** Muted display heading used above the timeline and growth log. */
export function MutedHeading({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        margin: '0 0 8px',
      }}
    >
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 'var(--text-md)',
          margin: 0,
          color: 'var(--text-muted)',
        }}
      >
        {children}
      </h3>
      {note ? <span style={{ fontSize: 10, color: 'var(--text-subtle)' }}>{note}</span> : null}
    </div>
  );
}

/** Times, counts, amounts — always the tabular face. */
export function Mono({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span
      style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', ...style }}
    >
      {children}
    </span>
  );
}

/** Standard screen padding, matching the prototype's 18px gutters. */
export function Screen({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ padding: '18px 18px 26px', ...style }}>{children}</div>;
}

/** The parent avatar circle: マ on sky, パ on sage. */
export function Avatar({
  initial,
  color,
  size = 30,
  style,
}: {
  initial: string;
  color: string;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden
      style={{
        flex: 'none',
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: Math.round(size * 0.43),
        fontFamily: 'var(--font-display)',
        ...style,
      }}
    >
      {initial}
    </span>
  );
}

/**
 * Calm empty state. The design system is explicit that we nudge rather than
 * scold, so these read as an invitation, never as an error.
 */
export function EmptyState({
  icon,
  children,
  style,
}: {
  icon?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: '26px 18px',
        textAlign: 'center',
        color: 'var(--text-subtle)',
        fontSize: 'var(--text-sm)',
        lineHeight: 'var(--leading-cjk)',
        ...style,
      }}
    >
      {icon}
      <div>{children}</div>
    </div>
  );
}
